import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import {
  Email as EmailIcon,
  OpenInNew as OpenIcon,
  MouseOutlined as MouseIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { Campaign, EmailEvent, AlertInfo } from '../types';
import { analyticsApi, campaignApi, getDefaultAccountId } from '../services/api';

interface AnalyticsDashboardProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ showAlert }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState<Campaign | null>(null);
  // const [campaignAnalytics, setCampaignAnalytics] = useState<any>(null);
  const [followUpAnalytics, setFollowUpAnalytics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Load data from localStorage and API
  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const accountId = getDefaultAccountId();

      // Fetch campaigns from API
      const campaignsResponse = await campaignApi.getByAccount(accountId);
      const campaignsData = campaignsResponse.campaigns || [];
      setCampaigns(campaignsData.map((c: any) => ({
        ...c,
        createdAt: new Date(c.createdAt),
        sentAt: c.sentAt ? new Date(c.sentAt) : undefined
      })));

      // Fetch analytics events from API
      const eventsResponse = await analyticsApi.getEvents(accountId);
      const eventsData = eventsResponse.events || [];
      setEvents(eventsData.map((e: any) => ({
        ...e,
        timestamp: new Date(e.timestamp)
      })));

    } catch (error) {
      console.error('Error loading analytics:', error);
      showAlert('Failed to load analytics data. Please check your connection.', 'error');
      // Clear data on error instead of keeping stale data
      setCampaigns([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCampaigns = () => {
    if (selectedCampaign === 'all') return campaigns;
    return campaigns.filter(c => c.id === selectedCampaign);
  };

  const getFilteredEvents = () => {
    if (selectedCampaign === 'all') return events;
    return events.filter(e => e.campaignId === selectedCampaign);
  };

  const calculateOverallStats = () => {
    const filteredCampaigns = getFilteredCampaigns();
    const totalSent = filteredCampaigns.reduce((sum, c) => sum + (c.analytics?.totalSent || 0), 0);
    const totalOpened = filteredCampaigns.reduce((sum, c) => sum + (c.analytics?.totalOpened || 0), 0);
    const totalClicked = filteredCampaigns.reduce((sum, c) => sum + (c.analytics?.totalClicked || 0), 0);

    const openRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
    const clickRate = totalSent > 0 ? (totalClicked / totalSent) * 100 : 0;

    return {
      totalCampaigns: filteredCampaigns.length,
      totalSent,
      totalOpened,
      totalClicked,
      openRate: isNaN(openRate) ? 0 : openRate,
      clickRate: isNaN(clickRate) ? 0 : clickRate
    };
  };

  const getChartData = () => {
    return getFilteredCampaigns().map(campaign => ({
      name: campaign.name.length > 15 ? campaign.name.substring(0, 15) + '...' : campaign.name,
      sent: campaign.analytics?.totalSent || 0,
      opened: campaign.analytics?.totalOpened || 0,
      clicked: campaign.analytics?.totalClicked || 0,
      openRate: campaign.analytics?.openRate || 0,
      clickRate: campaign.analytics?.clickRate || 0
    }));
  };

  const getPieData = () => {
    const stats = calculateOverallStats();
    const notOpened = Math.max(0, stats.totalSent - stats.totalOpened);
    return [
      { name: 'Opened', value: stats.totalOpened || 0, color: '#4caf50' },
      { name: 'Not Opened', value: notOpened, color: '#f44336' }
    ];
  };

  const getRecentEvents = () => {
    return getFilteredEvents()
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 10);
  };

  const handleCampaignDetails = async (campaign: Campaign) => {
    setSelectedCampaignDetails(campaign);
    setDetailsOpen(true);

    // Load detailed analytics with follow-ups
    try {
      const detailedAnalytics = await analyticsApi.getCampaignDetails(campaign.id, true);
      // setCampaignAnalytics(detailedAnalytics.analytics);
      setFollowUpAnalytics(detailedAnalytics.followUps || []);
    } catch (error) {
      console.error('Error loading detailed analytics:', error);
      showAlert('Failed to load detailed analytics', 'error');
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'SENT': return 'success';
      case 'SENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getEventIcon = (eventType: EmailEvent['eventType']) => {
    switch (eventType) {
      case 'sent': return <EmailIcon fontSize="small" />;
      case 'opened': return <OpenIcon fontSize="small" />;
      case 'clicked': return <MouseIcon fontSize="small" />;
      default: return <EmailIcon fontSize="small" />;
    }
  };

  const overallStats = calculateOverallStats();

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 2 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading analytics...
        </Typography>
      </Box>
    );
  }

  const getClickPieData = () => {
    const stats = calculateOverallStats();
    const notClicked = Math.max(0, stats.totalSent - stats.totalClicked);
    return [
      { name: 'Clicked', value: stats.totalClicked || 0, color: '#4caf50' },
      { name: 'Not Clicked', value: notClicked, color: '#f44336' }
    ];
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 3
      }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and analyze your email campaign performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Campaign Filter</InputLabel>
            <Select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              label="Campaign Filter"
            >
              <MenuItem value="all">All Campaigns</MenuItem>
              {campaigns.map(campaign => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadAnalyticsData} sx={{ borderRadius: 2 }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>

        {/* Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #e3f2fd 0%, white 100%)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {overallStats.totalCampaigns}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Campaigns
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #e8f5e8 0%, white 100%)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {overallStats.totalSent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emails Sent
                  </Typography>
                </Box>
                <EmailIcon sx={{ fontSize: 40, color: '#388e3c' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff3e0 0%, white 100%)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {overallStats.openRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open Rate
                  </Typography>
                </Box>
                <OpenIcon sx={{ fontSize: 40, color: '#f57c00' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f3e5f5 0%, white 100%)',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {overallStats.clickRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click Rate
                  </Typography>
                </Box>
                <MouseIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Campaign Performance
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sent" fill="#2196f3" name="Sent" />
                  <Bar dataKey="opened" fill="#4caf50" name="Opened" />
                  <Bar dataKey="clicked" fill="#ff9800" name="Clicked" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Open Rate Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getPieData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                  >
                    {getPieData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Click Rate Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getClickPieData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                  >
                    {getClickPieData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Campaigns Table */}
        <Paper sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, fontWeight: 'bold' }}>
            Campaign Details
          </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Sent</TableCell>
                <TableCell align="right">Opened</TableCell>
                <TableCell align="right">Clicked</TableCell>
                <TableCell align="right">Open Rate</TableCell>
                <TableCell align="right">Click Rate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredCampaigns().map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={campaign.status} 
                      color={getStatusColor(campaign.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{campaign.analytics?.totalSent || 0}</TableCell>
                  <TableCell align="right">{campaign.analytics?.totalOpened || 0}</TableCell>
                  <TableCell align="right">{campaign.analytics?.totalClicked || 0}</TableCell>
                  <TableCell align="right">{campaign.analytics?.openRate ? campaign.analytics.openRate.toFixed(1) : '0.0'}%</TableCell>
                  <TableCell align="right">{campaign.analytics?.clickRate ? campaign.analytics.clickRate.toFixed(1) : '0.0'}%</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleCampaignDetails(campaign)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

        {/* Recent Events */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, fontWeight: 'bold' }}>
            Recent Activity
          </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getRecentEvents().map((event) => {
                const campaign = campaigns.find(c => c.id === event.campaignId);
                return (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getEventIcon(event.eventType)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {event.eventType}
                        </Typography>
                      </Box>
                      {event.linkUrl && (
                          <Typography  component={Link} href={event.linkUrl} target="_blank" variant="body1" >
                            {event.linkUrl}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell>{campaign?.name || 'Unknown'}</TableCell>
                    <TableCell>{event.recipientEmail}</TableCell>
                    <TableCell>{formatDate(event.timestamp)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </Box>

      {/* Campaign Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Campaign Details</DialogTitle>
        <DialogContent>
          {selectedCampaignDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedCampaignDetails.name}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status: <Chip label={selectedCampaignDetails.status} size="small" />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {formatDate(selectedCampaignDetails.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Recipients: {selectedCampaignDetails.recipients.length}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Sender: {selectedCampaignDetails.senderEmail}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Main Campaign Performance
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedCampaignDetails.analytics.totalSent}</Typography>
                      <Typography variant="caption">Sent</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedCampaignDetails.analytics.totalOpened}</Typography>
                      <Typography variant="caption">Opened</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedCampaignDetails.analytics.totalClicked}</Typography>
                      <Typography variant="caption">Clicked</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">{selectedCampaignDetails.analytics.openRate.toFixed(1)}%</Typography>
                      <Typography variant="caption">Open Rate</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Follow-up Analytics */}
              {followUpAnalytics.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Follow-up Campaigns
                  </Typography>
                  {followUpAnalytics.map((followUp, index) => (
                    <Box key={followUp.id} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Follow-up #{index + 1}: {followUp.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          label={followUp.status}
                          size="small"
                          color={getStatusColor(followUp.status)}
                        />
                        {followUp.sentAt && (
                          <Typography variant="caption" color="text.secondary">
                            Sent: {formatDate(new Date(followUp.sentAt))}
                          </Typography>
                        )}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" fontWeight="bold">
                              {followUp.analytics.totalSent}
                            </Typography>
                            <Typography variant="caption">Sent</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" fontWeight="bold">
                              {followUp.analytics.totalOpened}
                            </Typography>
                            <Typography variant="caption">Opened</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" fontWeight="bold">
                              {followUp.analytics.totalClicked}
                            </Typography>
                            <Typography variant="caption">Clicked</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
                            <Typography variant="body2" fontWeight="bold">
                              {followUp.analytics.openRate.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption">Open Rate</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {/* Combined Analytics Summary */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="primary.main">
                      Combined Campaign + Follow-ups Performance
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'white' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {selectedCampaignDetails.analytics.totalSent + followUpAnalytics.reduce((sum, f) => sum + f.analytics.totalSent, 0)}
                          </Typography>
                          <Typography variant="caption">Total Sent</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'white' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {selectedCampaignDetails.analytics.totalOpened + followUpAnalytics.reduce((sum, f) => sum + f.analytics.totalOpened, 0)}
                          </Typography>
                          <Typography variant="caption">Total Opened</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'white' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {selectedCampaignDetails.analytics.totalClicked + followUpAnalytics.reduce((sum, f) => sum + f.analytics.totalClicked, 0)}
                          </Typography>
                          <Typography variant="caption">Total Clicked</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={3}>
                        <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'white' }}>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {(() => {
                              const totalSent = selectedCampaignDetails.analytics.totalSent + followUpAnalytics.reduce((sum, f) => sum + f.analytics.totalSent, 0);
                              const totalOpened = selectedCampaignDetails.analytics.totalOpened + followUpAnalytics.reduce((sum, f) => sum + f.analytics.totalOpened, 0);
                              return totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0.0';
                            })()}%
                          </Typography>
                          <Typography variant="caption">Combined Open Rate</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
    
  );
};

export default AnalyticsDashboard;
